import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import Checkbox from "components/forms/Checkbox";
import { CheckMarkIcon, DownloadIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";
import { CorrigoField, CorrigoLineCard } from "components/CorrigoPasteField";
import { useToast } from "components/toast";

import { getCorrigoExport, acceptOnBehalf, getQuotePDF, markPostedToCorrigo, getCorrigoProgress, saveCorrigoProgress } from "api/Quotes";
import { getRfqMaintenanceContext } from "api/QuoteRequests";

/**
 * §3.9 #17 — Corrigo paste-export panel. Design reference: mockup 08
 * (08-corrigo-paste-export.svg). Staff-only panel on the Quote detail
 * page, shown only when the quote's RFQ is plan-tagged (Corrigo-managed
 * clients only — untagged quotes never see this).
 *
 * Scope for this pass:
 *   - Line-by-line paste cards with per-field Copy buttons (Category /
 *     Description / Qty / Rate), matching CorrigoPro's real four-field
 *     Quote form (MASTER_PLAN.md §3.2).
 *   - A checklist-style "attach PDF" reminder rather than a new PDF
 *     fetch/download path — the existing Overview tab already has the
 *     quote's PDF download/email actions; duplicating that fetch here
 *     would be redundant. This panel just reminds staff to do it as
 *     part of the Corrigo submission sequence.
 *   - A local-only "Submitted in Corrigo" checkbox — there's no backend
 *     field for this (it's a staff reminder, not state JSCRM tracks),
 *     so it resets on page reload by design. It exists to sequence the
 *     UI: Accept-on-behalf appears only after the box is checked, so
 *     staff can't skip straight to marking accepted without having
 *     gone through the copy-paste steps first.
 *   - Accept-on-behalf action, wired to the existing endpoint from
 *     Session 38 (§3.9 #8). This is the first UI trigger point for that
 *     endpoint — it existed backend-only until now.
 */
export default function CorrigoExportPanel({
  quote,
  onAccepted,
  onPosted,
}: {
  quote: any;
  onAccepted: () => void;
  onPosted?: () => void;
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState<any>(null);
  // SESSION 45 GAP-25 — per-field copy tracking. Keys are
  // `lineIdx:fieldName` (e.g. `0:category`, `2:rate`). Header progress
  // meter reads from this. Never persisted — a fresh open of the panel
  // means the staff member should re-verify the paste, which is the
  // right default; staleness would silently mis-claim completion.
  const [copiedFields, setCopiedFields] = useState<Set<string>>(new Set());
  // SESSION 45 GAP-27 — mockup 08 separates "pasted the lines" from
  // "clicked Submit in Corrigo" as two distinct Mark ✓ steps. Prior
  // shipped code collapsed them into one checkbox, so a quote whose
  // lines are pasted but never submitted looked identical to a
  // submitted one. Two flags = accurate audit intent.
  const [linesPastedInCorrigo, setLinesPastedInCorrigo] = useState(false);
  // A14 — Step 1 auto-checks once every line reads "✓ Complete" (all
  // four fields copied on every line). `autoCheckedRef` lets a staff
  // member still UNcheck it manually afterward without the effect
  // immediately re-checking: the effect only auto-checks on the
  // rising edge (not-complete → complete), never fights a manual toggle.
  const autoCheckedRef = React.useRef(false);
  const [submittedInCorrigo, setSubmittedInCorrigo] = useState(false);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  // A14.2 — in-panel quote-PDF download. Same fetch the Quote Overview
  // tab uses (getQuotePDF → res.result URL → anchor download), lifted here
  // so staff can grab the PDF for the Corrigo upload without leaving the
  // panel. Not a second PDF path; the exact same endpoint.
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [externalWoNumber, setExternalWoNumber] = useState("");
  // A12(a) — the WO number is already known (it is the work order this
  // quote came from); pre-fill it so staff never re-type it at
  // acceptance. Sourced from the maintenance context off the quote's RFQ
  // (the same context the banner reads), which now carries the WO
  // `externalId`. Seeded once and only while the field is still empty, so
  // it never clobbers a staff edit. The field stays editable.
  const woNumberPrefilledRef = React.useRef(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [accepting, setAccepting] = useState(false);
  // mockup 34 — "Mark posted" is the audit signal that lights the quote's
  // Posted(=Sent) rung on a work-order-fed plan. Distinct from accept-on-
  // behalf (the later FM-approved moment). `posted` reflects the latest
  // corrigo_quote_submission row the export endpoint returns, so the
  // panel shows already-posted state on reload.
  const [posting, setPosting] = useState(false);
  const [postedLocal, setPostedLocal] = useState<any>(null);
  // Session 65 item 8: persist the two MANUAL milestone toggles (Step 1
  // pasted / Step 2 submitted) across refresh. Deliberately NOT persisting
  // `copiedFields` here - the per-field copy set is intentionally
  // re-verified on each reopen (see its declaration), so only the
  // operator's explicit Mark-checked steps are remembered. `progressHydrated`
  // gates the save effect so it doesn't echo the just-loaded state back.
  const [progressHydrated, setProgressHydrated] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setProgressHydrated(false);
      const res = await getCorrigoExport({ data: { id: quote.id } });
      setExportData(res);
      // Hydrate the manual milestone toggles from saved progress (best-
      // effort). copiedFields is intentionally left to re-verify.
      try {
        const prog = await getCorrigoProgress({ data: { id: quote.id } });
        const p = prog?.progress;
        if (p) {
          if (typeof p.linesPastedInCorrigo === "boolean") {
            setLinesPastedInCorrigo(p.linesPastedInCorrigo);
            // If we restore a checked Step 1, don't let the auto-check
            // effect treat a later manual uncheck as a rising edge.
            if (p.linesPastedInCorrigo) autoCheckedRef.current = true;
          }
          if (typeof p.submittedInCorrigo === "boolean") {
            setSubmittedInCorrigo(p.submittedInCorrigo);
          }
        }
      } catch {
        // ignore - progress is a convenience, not required
      }
      setProgressHydrated(true);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [quote?.id]);

  // Session 65 item 8: persist the milestone toggles (debounced) once
  // hydration is done. Last-write-wins server-side on (company, quote).
  // UI-only; drives no quote state (accept-on-behalf remains the real
  // status moment).
  useEffect(() => {
    if (!progressHydrated || !quote?.id) return;
    const t = setTimeout(() => {
      void saveCorrigoProgress({
        data: {
          id: quote.id,
          progress: { linesPastedInCorrigo, submittedInCorrigo },
        },
      });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [linesPastedInCorrigo, submittedInCorrigo, progressHydrated, quote?.id]);

  // A12(a) — pre-fill the Corrigo WO # from the work order behind this
  // quote. One fetch, keyed to the quote's RFQ; seeds `externalWoNumber`
  // only if the operator has not already typed one. Non-fatal — a plain
  // (non-WO) quote returns null and the field simply stays blank.
  const prefillRfqId = quote?.quoteRequest?.id ?? null;
  useEffect(() => {
    if (woNumberPrefilledRef.current) return;
    if (!prefillRfqId) return;
    let alive = true;
    (async () => {
      const ctx = await getRfqMaintenanceContext({ rfqId: prefillRfqId });
      if (!alive) return;
      const woNumber = ctx?.externalId ? String(ctx.externalId) : "";
      if (woNumber && !externalWoNumber.trim()) {
        setExternalWoNumber(woNumber);
      }
      woNumberPrefilledRef.current = true;
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, [prefillRfqId]);

  // A14 — auto-check Step 1 the moment every line is fully copied. Reads
  // completion straight off copiedFields + exportData rather than the
  // render-local `allLinesComplete` (an effect can't see that). Fires
  // only on the rising edge via autoCheckedRef, so a staff member who
  // deliberately unchecks Step 1 afterward is not overridden.
  useEffect(() => {
    const lines = exportData?.lines;
    if (!Array.isArray(lines) || lines.length === 0) return;
    const everyLineComplete = lines.every((_: any, idx: number) =>
      copiedFields.has(`${idx}:qty`) &&
      copiedFields.has(`${idx}:rate`),
    );
    if (everyLineComplete && !autoCheckedRef.current) {
      autoCheckedRef.current = true;
      setLinesPastedInCorrigo(true);
    }
  }, [copiedFields, exportData]);

  const copyField = async (value: string | number, label: string, fieldKey?: string) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.add(`${label} copied.`, "var(--theme-ui-colors-success)");
      // SESSION 45 GAP-25 — mark the field as copied for the progress
      // meter. `fieldKey` is optional so this helper can still be used
      // by non-tracked call sites (e.g. WO-completion defaults below
      // the line cards).
      if (fieldKey) {
        setCopiedFields((prev) => {
          const next = new Set(prev);
          next.add(fieldKey);
          return next;
        });
      }
    } catch {
      toast.add("Clipboard blocked.", "var(--theme-ui-colors-danger)");
    }
  };

  const canAcceptOnBehalf = [0, 3].includes(quote?.status); // New or Draft

  // A14.2 — mirrors Quote Overview's handleDownloadQuote exactly (same
  // getQuotePDF endpoint, same res.result URL + anchor download, same
  // filename convention), so there is one PDF path, not two. Lives here
  // so the operator can download the quote PDF for the Corrigo CruChat
  // upload right at the attach step, instead of routing back to Overview.
  const downloadQuotePdf = async () => {
    setPdfDownloading(true);
    try {
      const res = await getQuotePDF({ data: { id: quote.id } });
      if (!res?.result) {
        toast.add("Could not generate the quote PDF.", "var(--theme-ui-colors-danger)");
        return;
      }
      const versionSuffix =
        quote.majorVersion > 1 || quote.minorVersion > 0
          ? quote.minorVersion > 0
            ? "-" + quote.majorVersion + "." + quote.minorVersion
            : "-" + quote.majorVersion
          : "";
      const link = document.createElement("a");
      link.href = res.result;
      link.download = `GMQ_${String(quote.sequence).padStart(5, "0")}${versionSuffix}_quote.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.add("Could not generate the quote PDF.", "var(--theme-ui-colors-danger)");
    }
    setPdfDownloading(false);
  };

  // mockup 34 — record that this quote was posted to Corrigo. Audit-only;
  // the backend writes a corrigo_quote_submission row and the WO's
  // progress bar reads it to light the quote's Posted(=Sent) rung. Does
  // NOT change quote status (that is accept-on-behalf, below).
  const markPosted = async () => {
    setPosting(true);
    const res = await markPostedToCorrigo({
      data: { id: quote.id },
      note: approvalNote.trim() || undefined,
    });
    setPosting(false);
    if (res?.success) {
      setPostedLocal(res.posted ?? { createdAt: new Date().toISOString() });
      toast.add("Quote marked posted to Corrigo.", "var(--theme-ui-colors-success)");
      // Bubble up so the WO case file reloads and the WhatsNext card
      // re-derives (posting flips the next step from "post to Corrigo"
      // to "accept on behalf") without a manual refresh.
      if (onPosted) onPosted();
    } else {
      toast.add(res?.message || "Error marking posted.", "var(--theme-ui-colors-danger)");
    }
  };

  const submitAcceptOnBehalf = async () => {
    if (!externalWoNumber.trim()) {
      toast.add("Corrigo WO # is required.", "var(--theme-ui-colors-danger)");
      return;
    }
    // A13 — the note is now OPTIONAL. The acceptance itself is the audit
    // record: the backend `acceptOnBehalf` writes a QuoteAcceptOnBehalf
    // row stamping the staff user (who), the timestamp (when), and the
    // external WO # regardless of whether a note is supplied
    // (`approvalNote ?? null`). The free-text note remains available for
    // capturing who approved and how, but the team found requiring it
    // unwarranted friction and the trail does not depend on it.
    setAccepting(true);
    const res = await acceptOnBehalf({
      data: { id: quote.id },
      externalWorkOrderNumber: externalWoNumber.trim(),
      approvalNote: approvalNote.trim(),
    });
    setAccepting(false);
    if (res?.success) {
      toast.add("Quote accepted on behalf of client.", "var(--theme-ui-colors-success)");
      setShowAcceptForm(false);
      onAccepted();
    } else {
      toast.add(res?.message || "Error accepting on behalf.", "var(--theme-ui-colors-danger)");
    }
  };

  if (loading) return null;
  if (!exportData?.lines?.length) return null;

  // mockup 34 — already-posted state: prefer the just-marked local value,
  // else the latest submission the export endpoint returned on load.
  const posted = postedLocal ?? exportData?.posted ?? null;

  // SESSION 45 GAP-25 — derive progress numbers from the copied set.
  // Per line: 4 fields (Category, Description, Qty, Rate). Total
  // fields = 4 * lines.length. Lines complete = lines where all 4
  // fields are copied. Percentage rounds to nearest whole.
  // Per-line copy targets reduced to Qty + Rate: in CorrigoPro the
  // Category/Item is a pre-populated dropdown the operator selects, and
  // our Description is just the item-name echo of that category - neither
  // is pasted. Only quantity and rate are typed. (Discount, when one
  // applies, is a single panel-level field below, not per line.)
  const FIELDS_PER_LINE = 2;
  const lineCount = exportData.lines.length;
  const totalFields = lineCount * FIELDS_PER_LINE;
  const linesComplete = exportData.lines.filter((_: any, idx: number) => {
    return (
      copiedFields.has(`${idx}:qty`) &&
      copiedFields.has(`${idx}:rate`)
    );
  }).length;
  // Only count line-scoped fields (not the WO-completion defaults)
  // toward the header meter, so the meter can't tick past 100%.
  const copiedLineFields = Array.from(copiedFields).filter((k) => {
    const [prefix] = k.split(":");
    return /^\d+$/.test(prefix);
  }).length;
  const progressPct = totalFields === 0 ? 0 : Math.round((copiedLineFields / totalFields) * 100);
  const allLinesComplete = linesComplete === lineCount && lineCount > 0;

  // Session 56 fidelity — the "next up" line. Mockup 08 emphasizes the
  // active line with a dark border so staff never lose their place in a
  // strict-order paste. It is the first line whose four fields are not
  // all copied; -1 (none) once every line is complete.
  const activeLineIdx = exportData.lines.findIndex(
    (_: any, idx: number) =>
      !(
        copiedFields.has(`${idx}:qty`) &&
        copiedFields.has(`${idx}:rate`)
      ),
  );

  return (
    <Box
      sx={{
        marginTop: "24px",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        background: "white",
      }}
    >
      <Box sx={{ padding: "16px 20px", background: "#FAFAF8", borderBottom: "1px solid", borderColor: "inputOutline" }}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
              Corrigo Export
            </H5>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
              {exportData.planName ? `${exportData.planName} · ` : ""}Copy each field into CorrigoPro&apos;s Quote form.
            </Subtext>
          </Box>
          {/* SESSION 45 GAP-25 — progress meter. Mockup 08 header:
           * `6 of 14 fields copied · 2 lines complete · 43%`. A
           * three-line quote is twelve strict-order copies; losing
           * your place means pasting a Rate into a Qty field on the
           * client's system. The meter answers "which one am I on?"
           * without staff having to mentally track it. */}
          <Box sx={{ minWidth: "180px", maxWidth: "260px" }}>
            <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
              <Subtext fontSize={"10px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
                PROGRESS
              </Subtext>
              <Span fontFamily={"Poppins"} fontSize={"12px"} fontWeight={600} color={allLinesComplete ? "#27500A" : themevals.colors.black}>
                {progressPct}%
              </Span>
            </Flex>
            <Box sx={{ height: "6px", background: mnt.bgBeige, borderRadius: "3px", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${progressPct}%`,
                  // Mockup 08 keeps the progress bar green at every fill
                  // level (was blue #5B8AC0 in progress). Green = mnt.green.
                  background: mnt.green,
                  transition: "width 180ms ease",
                }}
              />
            </Box>
            <Subtext fontSize={"10px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} color={themevals.colors.dkgrey}>
              {copiedLineFields} of {totalFields} fields copied · {linesComplete} of {lineCount} lines complete
            </Subtext>
          </Box>
        </Flex>
      </Box>

      <Box sx={{ padding: "16px 20px" }}>
        {exportData.lines.map((line: any, idx: number) => {
          // Per-line completion status. Only Qty + Rate are copy targets
          // now (Category is a pre-populated CorrigoPro dropdown; our
          // Description is the item-name echo of that category). The line
          // header still shows the item description as a LABEL so staff
          // know which CorrigoPro row they're on - it just isn't a copy
          // field. `✓ Complete` once both are copied, `N of 2` in progress.
          const perLineCount =
            (copiedFields.has(`${idx}:qty`) ? 1 : 0) +
            (copiedFields.has(`${idx}:rate`) ? 1 : 0);
          const isActive = idx === activeLineIdx;
          const rateStr = line.rate.toLocaleString("en-US", { style: "currency", currency: "USD" });
          return (
            <CorrigoLineCard
              key={idx}
              lineNumber={idx + 1}
              description={line.category || line.description}
              copiedCount={perLineCount}
              fieldsPerLine={FIELDS_PER_LINE}
              active={isActive}
              last={idx === lineCount - 1}
            >
              <CorrigoField
                label="Qty"
                value={line.qty}
                onCopy={() => copyField(line.qty, "Qty", `${idx}:qty`)}
                copied={copiedFields.has(`${idx}:qty`)}
                wide
              />
              <CorrigoField
                label="Rate"
                value={rateStr}
                onCopy={() => copyField(line.rate, "Rate", `${idx}:rate`)}
                copied={copiedFields.has(`${idx}:rate`)}
                wide
              />
            </CorrigoLineCard>
          );
        })}

        {/* Discount (Tyler's discount model). Only shown when a discount
            was actually applied to this quote - a single dollar figure to
            paste into CorrigoPro, since discount is quote-level, not
            per-line. Hidden entirely when there's nothing to copy. */}
        {exportData.discountAmount != null && exportData.discountAmount > 0 && (
          <Box sx={{ marginTop: "14px" }}>
            <CorrigoField
              label="Discount"
              value={Number(exportData.discountAmount).toLocaleString("en-US", { style: "currency", currency: "USD" })}
              onCopy={() => copyField(exportData.discountAmount, "Discount", "discount")}
              copied={copiedFields.has("discount")}
              wide
            />
          </Box>
        )}

        {(exportData.corrigoRepairCategoryDefault || exportData.corrigoRepairCodeDefault) && (
          <Box sx={{ marginTop: "14px", padding: "10px 12px", background: "#FAFAF8", borderRadius: "6px" }}>
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
              WO COMPLETION DEFAULTS (for the Invoice form, once work is done)
            </Subtext>
            <Flex sx={{ gap: "16px", marginTop: "6px", flexWrap: "wrap" }}>
              {exportData.corrigoRepairCategoryDefault && (
                <CorrigoField
                  label="Repair Category"
                  value={exportData.corrigoRepairCategoryDefault}
                  onCopy={() => copyField(exportData.corrigoRepairCategoryDefault, "Repair Category")}
                />
              )}
              {exportData.corrigoRepairCodeDefault && (
                <CorrigoField
                  label="Repair Code"
                  value={exportData.corrigoRepairCodeDefault}
                  onCopy={() => copyField(exportData.corrigoRepairCodeDefault, "Repair Code")}
                />
              )}
            </Flex>
          </Box>
        )}

        <Box sx={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid", borderColor: "inputOutline" }}>
          {/* SESSION 45 GAP-27 — mockup 08 splits the audit steps.
           * Step 4/5 in the mockup: (1) all lines pasted into Corrigo,
           * (2) Submit button clicked in Corrigo. Prior shipped
           * version collapsed both into one checkbox. Splitting them
           * distinguishes a quote whose lines are pasted but never
           * submitted from one that is actually live in Corrigo.
           * The second checkbox is disabled until the first is
           * checked so the two steps stay in order. */}
          <Checkbox
            label="Step 1: Pasted every line into the CorrigoPro Quote form"
            checked={linesPastedInCorrigo}
            onChange={() => setLinesPastedInCorrigo(!linesPastedInCorrigo)}
            labelSize="13px"
          />
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} marginBottom={"10px"}>
            Auto-checks once every line above reads &ldquo;✓ Complete.&rdquo; Uncheck it if you need to redo a paste.
          </Subtext>
          <Box
            sx={{
              opacity: linesPastedInCorrigo ? 1 : 0.5,
              pointerEvents: linesPastedInCorrigo ? "auto" : "none",
              transition: "opacity 150ms ease",
            }}
          >
            <Checkbox
              label="Step 2: Attached the quote PDF and clicked Submit in Corrigo"
              checked={submittedInCorrigo}
              onChange={() => setSubmittedInCorrigo(!submittedInCorrigo)}
              labelSize="13px"
            />
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} marginBottom={"10px"}>
              Download the quote PDF here, then attach it in Corrigo via CruChat &rarr; Add New Document (Area: Work Order, Type: Document, Visibility: All).
            </Subtext>
            <BasicButton
              type="button"
              outline
              scheme="secondary"
              onClick={downloadQuotePdf}
              $submitting={pdfDownloading}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor={themevals.colors.primary}
              keepText={true}
              styles={{ margin: "0" }}
            >
              {!pdfDownloading && <DownloadIcon fill={themevals.colors.dkgrey} width="14px" />}
              Download quote PDF
            </BasicButton>

            {/* mockup 34 — Mark posted: the audit signal that lights the
                quote's Posted(=Sent) rung on the WO progress bar. Posting
                IS the send on a Corrigo plan, so this sits at the attach/
                submit step. Distinct from accept-on-behalf below (the FM
                approval that follows). */}
            <Box sx={{ marginTop: "12px" }}>
              {posted ? (
                <Flex sx={{ alignItems: "center", gap: "8px" }}>
                  <CheckMarkIcon fill={mnt.green} width="14px" />
                  <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                    Marked posted to Corrigo on{" "}
                    {new Date(posted.createdAt).toLocaleDateString()}
                    {posted.note ? ` — “${posted.note}”` : ""}
                  </Span>
                </Flex>
              ) : (
                <BasicButton
                  type="button"
                  outline
                  scheme="secondary"
                  onClick={markPosted}
                  $submitting={posting}
                  $iconHeight="14px"
                  $iconWidth="14px"
                  $loadingColor={themevals.colors.primary}
                  keepText={true}
                  styles={{ margin: "0" }}
                >
                  {!posting && <CheckMarkIcon fill={themevals.colors.dkgrey} width="14px" />}
                  Mark posted to Corrigo
                </BasicButton>
              )}
            </Box>
          </Box>
        </Box>

        {submittedInCorrigo && canAcceptOnBehalf && (
          <Box sx={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid", borderColor: "inputOutline" }}>
            {!showAcceptForm ? (
              <BasicButton type="button" scheme="primary" onClick={() => setShowAcceptForm(true)}>
                Accept on behalf of client
              </BasicButton>
            ) : (
              <Box>
                <Subtext fontSize={"12px"} fontFamily={"Poppins"} marginTop={0} marginBottom={"10px"}>
                  Record that the FM approved this in CorrigoPro.
                </Subtext>
                <TextInput
                  name="externalWorkOrderNumber"
                  label="Corrigo WO #"
                  required
                  value={externalWoNumber}
                  onChange={(val: any) => setExternalWoNumber(val)}
                  placeholder="e.g. 374295-00000099.01"
                />
                <Subtext
                  fontSize={"11px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  marginTop={"4px"}
                >
                  Pre-filled from this work order. Edit it if the Corrigo WO # differs.
                </Subtext>
                <Box sx={{ marginTop: "12px" }}>
                  <TextInput
                    type="textarea"
                    name="approvalNote"
                    label="Notes"
                    rows={2}
                    value={approvalNote}
                    onChange={(val: any) => setApprovalNote(val)}
                    placeholder={'e.g. "Approved by Koert Larsen, Corrigo msg 7/23 2:14 PM"'}
                  />
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginTop={"4px"}
                  >
                    Optional. A good place to record who approved and where (email, Corrigo message, phone) — the acceptance itself is already logged with your name and the time.
                  </Subtext>
                </Box>
                <Flex sx={{ gap: "10px", marginTop: "14px" }}>
                  <BasicButton type="button" outline scheme="secondary" onClick={() => setShowAcceptForm(false)}>
                    Cancel
                  </BasicButton>
                  <BasicButton
                    type="button"
                    onClick={submitAcceptOnBehalf}
                    disabled={!externalWoNumber.trim()}
                    $submitting={accepting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!accepting && <CheckMarkIcon fill={"#fff"} />}
                    Confirm Accept on Behalf
                  </BasicButton>
                </Flex>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
